import { z } from 'zod';
import { ErrorCode } from '../../../../shared/error-codes';
export const PARTNER_MATERIAL_LOCALES = [
    'de',
    'en',
    'fr',
    'hu'
];
export const PartnerMaterialLocaleSchema = z.enum(PARTNER_MATERIAL_LOCALES);
export const PartnerMaterialKindSchema = z.enum([
    'file',
    'video'
]);
/**
 * Fixed rather than free-form: a partner reads the category in their own
 * language, so every category needs a translation the code ships.
 */ export const PARTNER_MATERIAL_CATEGORIES = [
    'salesDocuments',
    'presentations',
    'caseStudies',
    'productInfo'
];
export const PartnerMaterialCategorySchema = z.enum(PARTNER_MATERIAL_CATEGORIES);
export const PARTNER_MATERIAL_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const PARTNER_MATERIAL_FILE_FORMATS = [
    'pdf',
    'pptx',
    'docx',
    'xlsx'
];
export const PartnerMaterialFileFormatSchema = z.enum(PARTNER_MATERIAL_FILE_FORMATS);
/** Office formats a partner hands on to a prospect, by their short name. */ export const PARTNER_MATERIAL_CONTENT_TYPES = {
    pdf: 'application/pdf',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};
/** The format of a content type, or null when partners may not receive it. */ export function partnerMaterialFormatOf(contentType) {
    return PARTNER_MATERIAL_FILE_FORMATS.find((format)=>PARTNER_MATERIAL_CONTENT_TYPES[format] === contentType) ?? null;
}
/** How long a published or re-uploaded material counts as new for partners. */ export const PARTNER_MATERIAL_NEW_DAYS = 30;
/**
 * Where a partner's language is missing: English first, as the language most
 * partners read, then German, as the language BLIKS IO writes in.
 */ export const PARTNER_MATERIAL_FALLBACK_LOCALES = [
    'en',
    'de'
];
/** The wanted language if available, else the fallbacks, else whatever there is. */ export function pickPartnerMaterialLocale(available, wanted) {
    const order = [
        ...wanted ? [
            wanted
        ] : [],
        ...PARTNER_MATERIAL_FALLBACK_LOCALES
    ];
    return order.find((locale)=>available.includes(locale)) ?? available[0] ?? null;
}
/** German is the text BLIKS IO always writes first, so it is the one required text. */ export const PartnerMaterialTextSchema = z.object({
    de: z.string().trim().min(1, ErrorCode.FIELD_REQUIRED),
    en: z.string().trim().optional(),
    fr: z.string().trim().optional(),
    hu: z.string().trim().optional()
});
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
/**
 * The video id of a YouTube watch, short-link, shorts or embed URL, or null.
 * Only the id is stored: the embed is always rebuilt on youtube-nocookie.com,
 * so a pasted URL can never smuggle another host into the partner page.
 */ export function parseYouTubeVideoId(input) {
    let url;
    try {
        url = new URL(input.trim());
    } catch  {
        return null;
    }
    const host = url.hostname.replace(/^www\.|^m\./, '');
    const candidate = host === 'youtu.be' ? url.pathname.slice(1) : host === 'youtube.com' || host === 'youtube-nocookie.com' ? url.pathname === '/watch' ? url.searchParams.get('v') : url.pathname.match(/^\/(?:shorts|embed)\/([^/]+)/)?.[1] : null;
    return candidate && YOUTUBE_ID.test(candidate) ? candidate : null;
}
export const PartnerMaterialVideoUrlSchema = z.string().refine((value)=>parseYouTubeVideoId(value) !== null, ErrorCode.FIELD_INVALID_URL);
export const PartnerMaterialFileSchema = z.object({
    fileName: z.string(),
    format: PartnerMaterialFileFormatSchema,
    size: z.number(),
    uploadedAt: z.string()
});
/** The superadmin view: everything, including inactive entries and file details. */ export const PartnerMaterialAdminSchema = z.object({
    _id: z.string(),
    kind: PartnerMaterialKindSchema,
    category: PartnerMaterialCategorySchema,
    title: PartnerMaterialTextSchema,
    description: PartnerMaterialTextSchema.partial().optional(),
    videoId: z.string().optional(),
    files: z.partialRecord(PartnerMaterialLocaleSchema, PartnerMaterialFileSchema),
    /** Published means visible to partners; unpublished is a draft. */ active: z.boolean(),
    position: z.number(),
    /** Set on the first publish and kept when the material is hidden again. */ publishedAt: z.string().optional(),
    updatedAt: z.string().optional()
});
/** The partner view: active entries only, no storage details. */ export const PartnerMaterialSchema = z.object({
    _id: z.string(),
    kind: PartnerMaterialKindSchema,
    category: PartnerMaterialCategorySchema,
    title: PartnerMaterialTextSchema,
    description: PartnerMaterialTextSchema.partial().optional(),
    videoId: z.string().optional(),
    /** Format and size per downloadable language; empty for videos. */ files: z.partialRecord(PartnerMaterialLocaleSchema, PartnerMaterialFileSchema.pick({
        format: true,
        size: true
    })),
    /** The later of the first publish and the newest upload. */ changedAt: z.string()
});

//# sourceMappingURL=partner-material.schema.js.map